import { initializeApp, type FirebaseApp } from 'firebase/app';
import {
  getAuth,
  RecaptchaVerifier,
  signInWithPhoneNumber,
  signOut as firebaseSignOut,
  type Auth,
  type ConfirmationResult,
  type UserCredential,
} from 'firebase/auth';
import { env } from './env';
import { isIosWebKit } from './display-mode';  // local use by recaptcha(); re-exported below

let _app: FirebaseApp | null = null;
let _auth: Auth | null = null;

export function firebaseApp(): FirebaseApp {
  if (_app) return _app;
  const e = env();
  _app = initializeApp({
    apiKey: e.VITE_FIREBASE_API_KEY,
    authDomain: e.VITE_FIREBASE_AUTH_DOMAIN,
    projectId: e.VITE_FIREBASE_PROJECT_ID,
    appId: e.VITE_FIREBASE_APP_ID,
    messagingSenderId: e.VITE_FIREBASE_MESSAGING_SENDER_ID,
  });
  return _app;
}

export function firebaseAuth(): Auth {
  if (_auth) return _auth;
  _auth = getAuth(firebaseApp());
  return _auth;
}

/**
 * Detect iOS WebKit (Safari + all iOS-resident WebKit browsers like Chrome iOS,
 * Firefox iOS, Edge iOS — they all share the same WebKit engine).
 *
 * Why this matters: Firebase's invisible reCAPTCHA is unreliable under Safari's
 * Intelligent Tracking Prevention — third-party cookies/iframes get blocked
 * and the verifier throws `auth/captcha-check-failed` or hangs silently.
 * Falling back to the visible challenge ("I'm not a robot" checkbox) costs
 * one extra tap but works without ITP carve-outs.
 *
 * Chrome / Firefox / Edge on iOS are NOT Safari (their UA omits Safari-only
 * tokens like `CriOS`/`FxiOS`/`EdgiOS`) and use the same WebKit engine, so
 * they also get the visible flow.
 *
 * Canonical implementation lives in `./display-mode` (consolidated 2026-06-01
 * to share the helper with the InstallCard PWA flow). Behaviour-preserving.
 */
export { isIosWebKit } from './display-mode';

/**
 * Build (or reuse) the reCAPTCHA verifier. Invisible on Android/desktop for
 * a seamless tap-and-go flow; visible on iOS WebKit because the invisible
 * flow doesn't work under Safari ITP.
 */
let _recaptcha: RecaptchaVerifier | null = null;
export function recaptcha(): RecaptchaVerifier {
  if (_recaptcha) return _recaptcha;
  let container = document.getElementById('recaptcha-container');
  if (!container) {
    container = document.createElement('div');
    container.id = 'recaptcha-container';
    document.body.appendChild(container);
  }
  const useVisible = isIosWebKit();
  if (useVisible) {
    // Visible challenge needs height + visibility. Centered within the glass card.
    container.style.display = 'flex';
    container.style.justifyContent = 'center';
    container.style.margin = '12px 0 0';
    container.style.minHeight = '78px';
  } else {
    container.style.display = 'none';
  }
  _recaptcha = new RecaptchaVerifier(
    firebaseAuth(),
    'recaptcha-container',
    { size: useVisible ? 'normal' : 'invisible' },
  );
  return _recaptcha;
}

export async function requestPhoneOtp(phoneE164: string): Promise<ConfirmationResult> {
  // RecaptchaVerifier is single-use — Firebase invalidates it after one
  // signInWithPhoneNumber call. Clear and rebuild on every send so a
  // "resend OTP" tap (PhoneRoute UX) doesn't blow up with auth/internal-error.
  if (_recaptcha) {
    try { _recaptcha.clear(); } catch { /* noop */ }
    _recaptcha = null;
  }
  const verifier = recaptcha();
  return signInWithPhoneNumber(firebaseAuth(), phoneE164, verifier);
}

export async function confirmOtpAndGetIdToken(
  confirmation: ConfirmationResult,
  code: string,
): Promise<{ idToken: string; cred: UserCredential }> {
  const cred = await confirmation.confirm(code);
  const idToken = await cred.user.getIdToken(/* forceRefresh */ false);
  return { idToken, cred };
}

export async function signOutFirebase(): Promise<void> {
  await firebaseSignOut(firebaseAuth());
  // Reset recaptcha so a fresh verifier mounts on next login
  if (_recaptcha) {
    try { _recaptcha.clear(); } catch { /* noop */ }
    _recaptcha = null;
  }
  // Drop the fallback container if recaptcha() created one (PhoneRoute will
  // re-create its own on next mount). Leaving it accumulates orphan nodes.
  document.getElementById('recaptcha-container')?.remove();
}
